import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { dataTableEvents } from 'src/app/core/constants/data-table-events';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { DOC_STATUS } from 'src/app/core/models/status.enum';
import { EventsServiceAction, EventsServiceData } from 'src/app/core/services/base-event-service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { decorateDetailsCell, syncActiveDetailsRow } from 'src/app/core/services/table.service';
import { selectUser } from 'src/app/core/store/selectors';
import { DataTableItem } from 'src/app/shared/components/data-table/client-side-data-table/client-side-data-table.component';
import { DateShortPipe } from 'src/app/shared/pipes/date-short.pipe';
import { DocStatusPipe } from 'src/app/shared/pipes/doc-status.pipe';
import { DocTypePipe } from 'src/app/shared/pipes/doc-type.pipe';
import { MyPositionPipe } from 'src/app/shared/pipes/my-position.pipe';
import { PartiesPipe } from 'src/app/shared/pipes/parties.pipe';
import { ThreeDotsMenuPipe } from 'src/app/shared/pipes/three-dots-menu.pipe';
import {
  getOneTableMenuItemHtml,
  isUserAdminOrLegal,
  isUserSalesOrProcurement,
} from 'src/app/shared/utils';
import { NotificationsActions } from 'src/app/modules/notifications/store/actions';
import { environment } from 'src/environment/environment';
import {
  DocumentContentType,
  DocumentDetailsField,
  DocumentOperation,
  DocumentReviewMetric,
  GetDocumentDto,
  getDocumentReviewMetric,
} from '../../models/documents.model';
import { DocumentsApiService } from '../../services/documents-api.service';
import { DocumentsActions } from '../../store/actions';
import { fromDocuments } from '../../store/selectors';
import { DocumentPageReviewProgress } from '../../store/state';
import { DocumentsListEventsService } from './documents-list-events.service';
import { UserDto, UserProfileType } from 'src/app/modules/login/services/user-api.service';

@UntilDestroy()
@Component({
  selector: 'app-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrl: './documents-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentsListComponent implements OnInit, OnChanges {
  @Input() public activeDocumentDetailsId: string | null = null;

  private readonly detailsCellClassName = 'documents-list__details-cell';
  private readonly activeDetailsRowClassName = 'documents-list__row--active';

  public data: GetDocumentDto[] | undefined;
  public columns!: DataTables.ColumnSettings[];
  public sort!: (string | number)[][];
  public dataTableMenuItems!: DataTableItem[];
  public refreshDataTable: BehaviorSubject<any> = new BehaviorSubject(null);
  public STATUS = DOC_STATUS;
  public constants = constants;
  public readonly tableId = 'documents-table-id';
  public reviewingDocumentsProgressById: Record<string, DocumentPageReviewProgress> = {};
  private userProfileType: UserProfileType | undefined;
  public user: UserDto | null | undefined;
  private reviewResponseDataByDocumentId: Record<
    string,
    GetDocumentDto['playbookComplianceScores']
  > = {};
  private activeReviewDocumentUniqueId: string | null = null;
  private refreshReviewCellTimeoutId: number | null = null;
  private canWriteDocuments = false;
  private canDeleteDocuments = false;
  private canReadReporting = false;
  private canReadDocumentProcessing = false;
  private canWriteDocumentProcessing = false;

  constructor(
    private readonly store$: Store,
    private readonly router: Router,
    public readonly documentsListEventsService: DocumentsListEventsService,
    public readonly threeDotsMenuPipe: ThreeDotsMenuPipe,
    public readonly dateShortPipe: DateShortPipe,
    private readonly translocoService: TranslocoService,
    public readonly docStatusPipe: DocStatusPipe,
    public readonly partiesPipe: PartiesPipe,
    public readonly docTypePipe: DocTypePipe,
    public readonly myPositionPipe: MyPositionPipe,
    public readonly documentsApiService: DocumentsApiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.documentsListEventsService.dataWasLoaded = false;
    this.initSubscriptions();
    this.fetchData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeDocumentDetailsId']) {
      syncActiveDetailsRow(
        this.tableId,
        this.activeDocumentDetailsId,
        this.activeDetailsRowClassName,
      );
    }
  }

  fetchData(): void {
    combineLatest([
      this.store$.select(fromDocuments.selectDocuments),
      this.store$.select(selectUser),
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canDelete$(PermissionResource.Documents),
      this.permissionService.canRead$(PermissionResource.Reporting),
      this.permissionService.canRead$(PermissionResource.DocumentProcessing),
      this.permissionService.canWrite$(PermissionResource.DocumentProcessing),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          data,
          user,
          canWrite,
          canDelete,
          canReadReporting,
          canReadDocumentProcessing,
          canWriteDocumentProcessing,
        ]) => {
          this.user = user;
          this.canWriteDocuments = canWrite;
          this.canDeleteDocuments = canDelete;
          this.canReadReporting = canReadReporting;
          this.canReadDocumentProcessing = canReadDocumentProcessing;
          this.canWriteDocumentProcessing = canWriteDocumentProcessing;
          this.initTable(data);
          this.cdr.detectChanges();

          setTimeout(() => {
            this.refreshDataTable.next(this.columns);
            this.cdr.detectChanges();
            syncActiveDetailsRow(
              this.tableId,
              this.activeDocumentDetailsId,
              this.activeDetailsRowClassName,
            );
          }, 1);
        },
      );
  }

  initSubscriptions(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.data) {
        this.initTable(this.data);
        this.refreshDataTable.next(this.columns);
        syncActiveDetailsRow(
          this.tableId,
          this.activeDocumentDetailsId,
          this.activeDetailsRowClassName,
        );
      }
    });

    this.documentsListEventsService
      .getSubject()
      .pipe(untilDestroyed(this))
      .subscribe((data: EventsServiceData) => {
        switch (data.action) {
          case EventsServiceAction.View: {
            this.nameClick(data.id);
            break;
          }
          case EventsServiceAction.ReviewLink: {
            this.reviewLink(data.id);
            break;
          }
          case EventsServiceAction.SendReport: {
            this.sendReport(data.id);
            break;
          }
          case EventsServiceAction.RequestSignatures: {
            this.requestSignatures(data.id);
            break;
          }
          case EventsServiceAction.Download: {
            this.downloadDocument(data.id);
            break;
          }
          case EventsServiceAction.EditInDesktop: {
            this.editInDesktop(data.id);
            break;
          }
          case EventsServiceAction.RelaunchReview: {
            this.relaunchReview(data.id);
            break;
          }
          case EventsServiceAction.ReviewDocument: {
            this.reviewDocument(data.id);
            break;
          }
          case EventsServiceAction.QueryLink: {
            this.queryLink(data.id);
            break;
          }
          case EventsServiceAction.AddClausesLink: {
            this.addClausesLink(data.id);
            break;
          }
          case EventsServiceAction.Delete: {
            this.delete(data.id);
            break;
          }
          case EventsServiceAction.NameClick: {
            this.processNameClick(data.id);
            break;
          }
        }
      });

    this.documentsListEventsService
      .getDataSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        syncActiveDetailsRow(
          this.tableId,
          this.activeDocumentDetailsId,
          this.activeDetailsRowClassName,
        );
      });

    combineLatest([
      this.store$.select(fromDocuments.selectActivePageReviewDocumentUniqueId),
      this.store$.select(fromDocuments.selectPageReviewProgressByDocumentId),
      this.store$.select(fromDocuments.selectPageReviewResponseDataByDocumentId),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          activeReviewDocumentUniqueId,
          reviewingDocumentsProgressById,
          reviewResponseDataByDocumentId,
        ]) => {
          this.activeReviewDocumentUniqueId = activeReviewDocumentUniqueId;
          this.reviewingDocumentsProgressById = reviewingDocumentsProgressById;
          this.reviewResponseDataByDocumentId = reviewResponseDataByDocumentId;
          this.refreshReviewCell();
        },
      );
  }

  editInDesktop(id: string): void {
    if (!this.canWriteDocuments) {
      return;
    }

    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === id);

    if (document) {
      window.open(`ms-word:ofe|u|${document.sharedLink}`, '_blank');
    }
  }

  relaunchReview(uniqueId: string): void {
    this.reviewDocument(uniqueId);
  }

  sendReport(id: string): void {
    if (!this.canReadReporting) {
      return;
    }

    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === id);

    if (document) {
      this.documentsListEventsService.onDetails({
        uniqueId: document.uniqueId,
        documentName: document.name,
        focusField: DocumentDetailsField.Report,
      });
    }
  }

  requestSignatures(uniqueId: string): void {
    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === uniqueId);

    if (document) {
      this.documentsListEventsService.onDetails({
        uniqueId: document.uniqueId,
        documentName: document.name,
        focusField: DocumentDetailsField.SignatureRequest,
      });
    }
  }

  downloadDocument(uniqueId: string): void {
    const fileName =
      this.data?.find((item: GetDocumentDto) => item.uniqueId === uniqueId)?.name ||
      'document.docx';

    this.store$.dispatch(DocumentsActions.downloadDocument({ uniqueId, fileName }));
  }

  reviewLink(id: string): void {
    if (!this.canReadDocumentProcessing) {
      return;
    }

    this.router.navigate([`documents/${id}`], { state: { tab: DocumentOperation.Review } });
  }

  nameClick(id: string): void {
    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === id);

    if (document) {
      window.open(document.directLink, '_blank');
    }
  }

  queryLink(id: string): void {
    if (!this.canReadDocumentProcessing) {
      return;
    }

    this.router.navigate([`documents/${id}`], { state: { tab: DocumentOperation.Query } });
  }

  addClausesLink(id: string): void {
    if (!this.canWriteDocuments || !this.canWriteDocumentProcessing) {
      return;
    }

    this.router.navigate([`documents/${id}`], { state: { tab: DocumentOperation.AddClauses } });
  }

  delete(uniqueId: string): void {
    if (!this.canDeleteDocuments) {
      return;
    }

    this.store$.dispatch(DocumentsActions.deleteDocument({ uniqueId }));
  }

  processNameClick(uniqueId: string): void {
    if (!this.canReadDocumentProcessing) {
      return;
    }

    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === uniqueId);

    if (document) {
      window.open(`${environment.reviewServerUrl}/home?docGuid=${uniqueId}`, '_blank');
    }
  }

  initColumns(): void {
    const that = this;

    this.columns = Object.assign([
      ...(this.canWriteDocuments || this.canDeleteDocuments
        ? [
            {
              title: '<input type="checkbox" id="selectAll">',
              data: 'uniqueId',
              searchable: false,
              orderable: false,
              width: '30px',
              className: 'select-column',
              render: function (data: any, type: any, full: any) {
                return `<input type="checkbox" id="selectItem" ${dataTableEvents.common.checkedId}="${full.uniqueId}">`;
              },
            },
          ]
        : []),
      {
        title: this.translocoService.translate('columns.docName'),
        data: 'name',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(cell, rowData.uniqueId, undefined, this.detailsCellClassName);
        },
        render: function (data: any, type: any, full: any) {
          return `<a name-click='${full.uniqueId}'>${data}</a>`;
        },
      },
      {
        title: this.translocoService.translate('columns.docType'),
        data: 'type',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(
            cell,
            rowData.uniqueId,
            DocumentDetailsField.DocumentType,
            this.detailsCellClassName,
          );
        },
        render: function (data: any, type: any, full: any) {
          const docTypePipe = new DocTypePipe(that.store$);
          let docTypeName = '';

          docTypePipe.transform(data).subscribe((value) => {
            docTypeName = value;
          });

          return docTypeName;
        },
      },
      {
        title: this.translocoService.translate('columns.lastEdited'),
        data: 'lastModifiedAt',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(cell, rowData.uniqueId, undefined, this.detailsCellClassName);
        },
        render: {
          display: function (data: any, type: any, full: any) {
            const date = that.dateShortPipe.transform(data);
            return `<span title="${full.lastModifiedBy}"> ${date} </span>`;
          },
          sort: function (data: any, type: any, full: any) {
            return new Date(data).getTime();
          },
        },
      },
      {
        title: this.translocoService.translate('columns.counterparties'),
        data: 'counterparties',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(
            cell,
            rowData.uniqueId,
            DocumentDetailsField.Counterparties,
            this.detailsCellClassName,
          );
        },
        render: function (data: any, type: any, full: any) {
          return `${that.partiesPipe.transform(data)}`;
        },
      },
      {
        title: this.translocoService.translate('columns.myPosition'),
        data: 'myPosition',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(
            cell,
            rowData.uniqueId,
            DocumentDetailsField.Position,
            this.detailsCellClassName,
          );
        },
        render: function (data: any, type: any, full: any) {
          return `${that.myPositionPipe.transform(data)}`;
        },
      },
      {
        title: this.translocoService.translate('columns.status'),
        data: 'status',
        width: '80px',
        createdCell: (cell: Node, cellData: any, rowData: GetDocumentDto) => {
          decorateDetailsCell(
            cell,
            rowData.uniqueId,
            DocumentDetailsField.Status,
            this.detailsCellClassName,
          );
        },
        render: function (data: any, type: any, full: any) {
          return that.docStatusPipe.transform(data);
        },
      },
      {
        title: 'Score',
        data: null,
        searchable: false,
        orderable: false,
        className: 'documents-list__score-column',
        render: function (data: any, type: any, full: any, meta: DataTables.CellMetaSettings) {
          return that.getMockScoreHtml(meta.row, full);
        },
      },
      {
        title: 'Playbook Compliance',
        data: null,
        searchable: false,
        orderable: false,
        className: 'documents-list__playbook-compliance-column',
        render: function (data: any, type: any, full: any, meta: DataTables.CellMetaSettings) {
          return that.getMockPlaybookComplianceHtml(meta.row, full);
        },
      },
      {
        width: '50px',
        data: 'uniqueId',
        searchable: false,
        orderable: false,
        render: function (data: any, type: any, full: any) {
          return `<div>${that.threeDotsMenuPipe.transform(data, that.getDataTableMenuItems(full))}</div>`;
        },
      },
    ]);
  }

  getMockScoreHtml(rowIndex: number, document: GetDocumentDto): string {
    const metric = this.getDocumentTableMetric(document);

    if (metric.score === null) {
      return '<span class="documents-list__score-empty">-</span>';
    }

    const scoreClass =
      metric.score >= 80
        ? 'documents-list__score-badge--high'
        : metric.score >= 60
          ? 'documents-list__score-badge--medium'
          : 'documents-list__score-badge--low';

    return `<button type="button" class="documents-list__score-badge ${scoreClass} documents-list__details-button" ${dataTableEvents.common.details}="${document.uniqueId}" ${dataTableEvents.common.detailsField}="${DocumentDetailsField.PlaybookCompliance}">${metric.score}%</button>`;
  }

  getMockPlaybookComplianceHtml(rowIndex: number, document: GetDocumentDto): string {
    const metric = this.getDocumentTableMetric(document);
    const reviewProgress = this.getReviewProgress(document.uniqueId);

    if (reviewProgress !== undefined) {
      return `
        <span class="documents-list__reviewing-control">
          <span class="documents-list__reviewing-spinner"></span>
          <span class="documents-list__reviewing-label">${this.translocoService.translate('buttons.reviewing')}</span>
          <span class="documents-list__reviewing-progress">${reviewProgress}%</span>
        </span>
      `;
    }

    if (metric.needsReview) {
      if (!this.getAssignedPlaybookId(document)) {
        return `<button type="button" class="documents-list__mock-review-button" ${dataTableEvents.common.details}="${document.uniqueId}" ${dataTableEvents.common.detailsField}="${DocumentDetailsField.Playbook}"><span class="table-menu-icon icon-right_panel_open"></span> ${this.translocoService.translate('buttons.selectPlaybook')}</button>`;
      }

      const reviewDisabled = !!this.activeReviewDocumentUniqueId;
      const reviewDisabledClass = reviewDisabled
        ? ' documents-list__mock-review-button--disabled'
        : '';
      const reviewDisabledAttribute = reviewDisabled ? ' disabled' : '';

      return `<button type="button" class="documents-list__mock-review-button${reviewDisabledClass}" ${dataTableEvents.common.reviewDocument}="${document.uniqueId}"${reviewDisabledAttribute}><span class="table-menu-icon icon-text_compare"></span> ${this.translocoService.translate('buttons.review')}</button>`;
    }

    return `
      <button type="button" class="documents-list__compliance-badges documents-list__details-button" ${dataTableEvents.common.details}="${document.uniqueId}" ${dataTableEvents.common.detailsField}="${DocumentDetailsField.PlaybookCompliance}">
        <span class="documents-list__compliance-badge documents-list__compliance-badge--matched">${metric.matched}</span>
        <span class="documents-list__compliance-badge documents-list__compliance-badge--warnings">${metric.warnings}</span>
        <span class="documents-list__compliance-badge documents-list__compliance-badge--failed">${metric.failed}</span>
      </button>
    `;
  }

  private getDocumentTableMetric(document: GetDocumentDto): DocumentReviewMetric {
    return (
      getDocumentReviewMetric(
        this.reviewResponseDataByDocumentId[document.uniqueId] ||
          document.playbookComplianceScores ||
          document.reviewResponseData,
      ) || {
        score: null,
        matched: 0,
        warnings: 0,
        failed: 0,
        needsReview: true,
      }
    );
  }

  reviewDocument(uniqueId: string): void {
    const document = this.data?.find((item: GetDocumentDto) => item.uniqueId === uniqueId);

    if (!document) {
      return;
    }

    const assignedPlaybookId = this.getAssignedPlaybookId(document);

    if (!assignedPlaybookId) {
      this.documentsListEventsService.onDetails({
        uniqueId: document.uniqueId,
        documentName: document.name,
        focusField: DocumentDetailsField.Playbook,
      });
      return;
    }

    if (this.activeReviewDocumentUniqueId) {
      return;
    }

    this.store$.dispatch(
      DocumentsActions.startPageReview({
        documentUniqueId: document.uniqueId,
        assignedPlaybookId,
      }),
    );
  }

  private getAssignedPlaybookId(document: GetDocumentDto): string | null {
    return document.assignedPlaybookId || null;
  }

  private getReviewProgress(uniqueId: string): number | undefined {
    const progress = this.reviewingDocumentsProgressById[uniqueId];

    if (!progress) {
      return undefined;
    }

    if (!progress.totalPages) {
      return 0;
    }

    return Math.min(
      100,
      Math.max(0, Math.round((progress.pageNumber / progress.totalPages) * 100)),
    );
  }

  private refreshReviewCell(): void {
    if (!this.columns) {
      this.cdr.markForCheck();
      return;
    }

    if (this.refreshReviewCellTimeoutId !== null) {
      window.clearTimeout(this.refreshReviewCellTimeoutId);
    }

    this.refreshReviewCellTimeoutId = window.setTimeout(() => {
      this.refreshReviewCellTimeoutId = null;
      this.refreshDataTable.next(this.columns);
      this.cdr.markForCheck();
    }, 0);
  }

  getExtraTableMenuItems(data: any): string {
    let html = '';

    if (isUserSalesOrProcurement(this.userProfileType)) {
      html += getOneTableMenuItemHtml(
        data,
        dataTableEvents.common.sendReport,
        'outgoing_mail',
        this.translocoService.translate('actions.sendReport'),
      );
    }

    return html;
  }

  initTable(data: GetDocumentDto[] | null): void {
    this.initColumns();
    this.dataTableMenuItems = this.getDataTableMenuItems();
    this.sort = [[3, 'desc']];
    this.data = data || [];
  }

  getDataTableMenuItems(fullDoc?: GetDocumentDto): DataTableItem[] {
    const dataTableMenuItems: DataTableItem[] = [
      {
        title: this.translocoService.translate('actions.openInBrowser'),
        event: dataTableEvents.common.nameClick,
        icon: 'quick_reference_all',
        disabled: false,
        separatorAfter: !isUserAdminOrLegal(this.userProfileType),
      },
      {
        title: this.translocoService.translate('actions.details'),
        event: dataTableEvents.common.details,
        icon: 'right_panel_open',
        disabled: false,
      },
      {
        title: this.translocoService.translate('actions.editProperties'),
        event: dataTableEvents.common.editProperties,
        icon: 'edit_square',
        disabled: false,
        separatorAfter: true,
      },
      {
        title: 'Relaunch Review',
        event: dataTableEvents.common.relaunchReview,
        icon: 'text_compare',
        disabled: false,
      },
      {
        title: this.translocoService.translate('actions.sendReport'),
        event: dataTableEvents.common.sendReport,
        icon: 'outgoing_mail',
        disabled: false,
      },
      {
        title: this.translocoService.translate('actions.requestSignatures'),
        event: dataTableEvents.common.requestSignatures,
        icon: 'signature',
        disabled: false,
      },
      {
        title: this.translocoService.translate('actions.download'),
        event: dataTableEvents.common.download,
        icon: 'download',
        disabled: false,
        separatorAfter: true,
      },
      {
        title: this.translocoService.translate('actions.remove'),
        event: dataTableEvents.common.delete,
        icon: 'delete',
        disabled: false,
      },
    ];

    if (this.canWriteDocuments) {
      dataTableMenuItems.splice(6, 0, {
        title: this.translocoService.translate('actions.editInDesktop'),
        event: dataTableEvents.common.editInDesktop,
        icon: 'quick_reference_all',
        disabled: fullDoc?.contentType === DocumentContentType.PDF,
        separatorAfter: true,
      });
    }

    // AL-329 -> switched to disabled state, uncomment in case need to delete menu item
    // if (fullDoc?.contentType === DocumentContentType.PDF) {
    //   dataTableMenuItems = dataTableMenuItems.filter(
    //     (item) => item.event !== dataTableEvents.common.editInDesktop,
    //   );
    // }

    return dataTableMenuItems.filter((item) => {
      if (item.event === dataTableEvents.common.editProperties) {
        return this.canWriteDocuments;
      }

      if (item.event === dataTableEvents.common.delete) {
        return this.canDeleteDocuments;
      }

      if (item.event === dataTableEvents.common.sendReport) {
        return this.canReadReporting;
      }

      if (item.event === dataTableEvents.common.nameClick) {
        return this.canReadDocumentProcessing;
      }

      return true;
    });
  }

  get isDocsDisclaimer(): boolean {
    return this.user?.email.indexOf('@axa') !== -1;
  }
}
